import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { MessageSquare, Compass, Settings as SettingsIcon } from 'lucide-react';
// import { useState } from 'react'; // language toggle temporarily disabled
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { ResearchChat } from '../research/ResearchChat';
import { ResearchExplorer } from '../research/ResearchExplorer';
import { GET_RESEARCH_SETTINGS } from '../../graphql';
import { GetResearchSettingsResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';

export function ResearchLayout() {
  const navigate = useNavigate();
  // English-only for now; Arabic toggle commented out below.
  const lang = 'en';
  // const [lang, setLang] = useState<string>('en');

  const { accountId, isLoading: accountsLoading } = useAccount();

  const { data: settingsData, loading: settingsLoading } = useQuery<GetResearchSettingsResponse>(
    GET_RESEARCH_SETTINGS,
    {
      variables: { accountId },
      skip: !accountId,
    }
  );

  const settings = settingsData?.researchSettings;
  const isConfigured = Boolean(settings?.searchEndpoint && settings?.searchApiKey);
  const isEnabled = settings?.isActive !== false;
  const loading = accountsLoading || settingsLoading;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center text-cmp-ink-2">
          Loading research...
        </div>
      );
    }

    if (!accountId) {
      return (
        <div className="flex-1 flex items-center justify-center text-cmp-ink-2">
          No account found.
        </div>
      );
    }

    if (!isConfigured || !isEnabled) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 px-6">
          <SettingsIcon className="w-10 h-10 text-primary" />
          <div>
            <h3 className="text-cmp-ink mb-2">
              {!isConfigured ? 'Research is not configured yet' : 'Research is disabled'}
            </h3>
            <p className="text-cmp-ink-2 max-w-md">
              {!isConfigured
                ? 'Azure AI Search is not configured on the platform. Set AZURE_SEARCH_ENDPOINT and AZURE_SEARCH_API_KEY on the API server, then restart.'
                : 'Research has been turned off for this account. Re-enable it in settings.'}
            </p>
          </div>
          {isConfigured && (
            <Button
              onClick={() => navigate('/settings')}
              className="bg-button-primary text-button-primary"
            >
              Go to Settings
            </Button>
          )}
        </div>
      );
    }

    return (
      <Tabs defaultValue="chat" className="flex-1 flex flex-col min-h-0">
        <div className="flex-shrink-0 flex items-center justify-between flex-wrap gap-3">
          <TabsList className="bg-core-dark">
            <TabsTrigger value="chat" className="gap-2 data-[state=active]:bg-form-dark">
              <MessageSquare className="w-4 h-4" />
              Ask the Archive
            </TabsTrigger>
            <TabsTrigger value="explore" className="gap-2 data-[state=active]:bg-form-dark">
              <Compass className="w-4 h-4" />
              Explore Content
            </TabsTrigger>
          </TabsList>

          {/* Language toggle hidden — English only for now.
          <div className="flex items-center rounded-lg overflow-hidden border border-cmp-line-2 text-sm">
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 transition-colors ${
                lang === 'en' ? 'bg-primary text-onfill' : 'bg-core-dark text-cmp-ink-2'
              }`}
            >
              English
            </button>
            <button
              onClick={() => setLang('ar')}
              className={`px-3 py-1.5 transition-colors ${
                lang === 'ar' ? 'bg-primary text-onfill' : 'bg-core-dark text-cmp-ink-2'
              }`}
            >
              العربية
            </button>
          </div>
          */}
        </div>

        <TabsContent value="chat" className="flex-1 min-h-0 mt-4">
          <ResearchChat accountId={accountId} lang={lang} />
        </TabsContent>
        <TabsContent value="explore" className="flex-1 min-h-0 mt-4">
          <ResearchExplorer accountId={accountId} lang={lang} />
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="m-0 w-full h-full relative overflow-hidden box-border bg-primary-background flex flex-col">
            <div className="ink-frame">
              <div className="flex-shrink-0 mb-4">
                <h1 className="mb-1">Research</h1>
                <p className="text-muted-foreground">
                  Ask questions of your indexed archive and explore extracted facts and photos.
                </p>
              </div>
              {renderBody()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
